import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api, { API_BASE } from "../lib/api";
import {
  Button, Input, Textarea, Label, FormField, Select,
  Dialog, Sheet,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  SearchInput, PageHeader,
  StatCard, Spinner, Empty, Badge,
  Card, CardContent, CardHeader, CardTitle,
} from "../components/ui";
import {
  Plus, Edit2, Trash2, Upload, X, Package,
  Eye, TrendingUp, DollarSign, ShoppingCart, BarChart2, Printer,
} from "lucide-react";

const STATIC_BASE = (API_BASE || "").replace(/\/api\/?$/, "");

function fmt(n) {
  return Number(n || 0).toLocaleString("es-PY");
}

// ─── TABS ────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "productos",    label: "Productos" },
  { id: "clientes",     label: "Clientes" },
  { id: "presupuestos", label: "Presupuestos" },
  { id: "ventas",       label: "Ventas" },
  { id: "finanzas",     label: "Finanzas" },
];

// ─── TAB SELECTOR ────────────────────────────────────────────────────────────
function TabBar({ active, onChange }) {
  return (
    <div className="flex border-b border-border overflow-x-auto shrink-0">
      {TABS.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
            active === t.id
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── PRODUCTOS TAB ───────────────────────────────────────────────────────────
function ProductosTab() {
  const [list,      setList]      = useState([]);
  const [cats,      setCats]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [open,      setOpen]      = useState(false);
  const [edit,      setEdit]      = useState(null);
  const [imgDetail, setImgDetail] = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const emptyForm = () => ({
    codigo: "", nombre: "", descripcion: "", categoria_id: "",
    stock_actual: 0, stock_minimo: 0, precio_costo: 0, precio_venta: 0, unidad: "UND",
  });
  const [form, setForm] = useState(emptyForm());
  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        api.get("/bazar/productos", { params: search ? { q: search } : {} }),
        api.get("/bazar/categorias"),
      ]);
      setList(p.data);
      setCats(c.data);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEdit(null);
    setForm(emptyForm());
    setOpen(true);
  }
  function openEdit(p) {
    setEdit(p);
    setForm({
      codigo: p.codigo ?? "", nombre: p.nombre, descripcion: p.descripcion ?? "",
      categoria_id: p.categoria_id ?? "",
      stock_actual: p.stock_actual, stock_minimo: p.stock_minimo,
      precio_costo: p.precio_costo, precio_venta: p.precio_venta, unidad: p.unidad,
    });
    setOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        codigo:       form.codigo || null,
        categoria_id: form.categoria_id ? Number(form.categoria_id) : null,
        stock_actual: Number(form.stock_actual),
        stock_minimo: Number(form.stock_minimo),
        precio_costo: Number(form.precio_costo),
        precio_venta: Number(form.precio_venta),
      };
      if (edit) {
        await api.put(`/bazar/productos/${edit.id}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/bazar/productos", payload);
        toast.success("Producto creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(p) {
    if (!window.confirm(`¿Eliminar "${p.nombre}"?`)) return;
    await api.delete(`/bazar/productos/${p.id}`);
    toast.success("Producto eliminado");
    load();
  }

  async function openImages(p) {
    const r = await api.get(`/bazar/productos/${p.id}`);
    setImgDetail(r.data);
  }

  async function handleUploadImage(e) {
    const file = e.target.files?.[0];
    if (!file || !imgDetail) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/bazar/productos/${imgDetail.id}/imagenes`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const r = await api.get(`/bazar/productos/${imgDetail.id}`);
      setImgDetail(r.data);
      load();
      toast.success("Imagen subida");
    } catch {
      toast.error("Error al subir imagen");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleDeleteImage(imgId) {
    await api.delete(`/bazar/productos/${imgDetail.id}/imagenes/${imgId}`);
    const r = await api.get(`/bazar/productos/${imgDetail.id}`);
    setImgDetail(r.data);
    load();
  }

  if (loading) return <div className="flex justify-center p-16"><Spinner className="w-8 h-8" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar productos…" className="flex-1 sm:max-w-xs" />
        <Button onClick={openCreate}><Plus className="w-4 h-4" />Nuevo producto</Button>
      </div>

      {list.length === 0 ? (
        <Empty message="Sin productos registrados" icon={Package} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {list.map((p) => (
            <Card key={p.id} className="overflow-hidden flex flex-col">
              <div
                className="relative h-40 bg-muted flex items-center justify-center cursor-pointer"
                onClick={() => openImages(p)}
              >
                {p.imagenes?.length > 0 ? (
                  <img
                    src={`${STATIC_BASE}${p.imagenes[0].url}`}
                    alt={p.nombre}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Package className="w-12 h-12 text-muted-foreground/25" />
                )}
                {p.imagenes?.length > 1 && (
                  <span className="absolute bottom-1.5 right-1.5 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                    +{p.imagenes.length - 1}
                  </span>
                )}
              </div>
              <CardContent className="p-3 flex flex-col flex-1 gap-2">
                <div>
                  <p className="font-medium text-sm leading-tight line-clamp-2">{p.nombre}</p>
                  {p.categoria_nombre && (
                    <p className="text-xs text-muted-foreground mt-0.5">{p.categoria_nombre}</p>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span>Stock: <strong>{p.stock_actual}</strong> {p.unidad}</span>
                  {Number(p.stock_actual) <= Number(p.stock_minimo) && (
                    <Badge variant="destructive">Stock bajo</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Costo: {fmt(p.precio_costo)}</span>
                  <span className="font-semibold text-primary">Venta: {fmt(p.precio_venta)}</span>
                </div>
                <div className="flex gap-1 mt-auto pt-1">
                  <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => openImages(p)}>
                    Imágenes
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)}>
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(p)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={open} onClose={() => setOpen(false)} title={edit ? "Editar producto" : "Nuevo producto"} size="lg">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Nombre *" className="col-span-full">
              <Input value={form.nombre} onChange={(e) => s("nombre", e.target.value)} required />
            </FormField>
            <FormField label="Código">
              <Input value={form.codigo} onChange={(e) => s("codigo", e.target.value)} placeholder="SKU-001" />
            </FormField>
            <FormField label="Unidad">
              <Input value={form.unidad} onChange={(e) => s("unidad", e.target.value)} placeholder="UND, KG, LT…" />
            </FormField>
            <FormField label="Categoría" className="col-span-full">
              <Select value={form.categoria_id} onChange={(e) => s("categoria_id", e.target.value)}>
                <option value="">Sin categoría</option>
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </Select>
            </FormField>
          </div>
          <FormField label="Descripción">
            <Textarea
              value={form.descripcion}
              onChange={(e) => s("descripcion", e.target.value)}
              rows={2}
              placeholder="Descripción opcional del producto"
            />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Precio de costo">
              <Input type="number" step="0.01" min="0" value={form.precio_costo}
                onChange={(e) => s("precio_costo", e.target.value)} />
            </FormField>
            <FormField label="Precio de venta">
              <Input type="number" step="0.01" min="0" value={form.precio_venta}
                onChange={(e) => s("precio_venta", e.target.value)} />
            </FormField>
            <FormField label="Stock actual">
              <Input type="number" step="0.001" min="0" value={form.stock_actual}
                onChange={(e) => s("stock_actual", e.target.value)} />
            </FormField>
            <FormField label="Stock mínimo">
              <Input type="number" step="0.001" min="0" value={form.stock_minimo}
                onChange={(e) => s("stock_minimo", e.target.value)} />
            </FormField>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={saving}>Guardar</Button>
          </div>
        </form>
      </Dialog>

      {/* Dialog imágenes */}
      {imgDetail && (
        <Dialog open={!!imgDetail} onClose={() => setImgDetail(null)} title={`Imágenes — ${imgDetail.nombre}`}>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {imgDetail.imagenes?.map((img) => (
                <div key={img.id} className="relative group aspect-square">
                  <img
                    src={`${STATIC_BASE}${img.url}`}
                    alt=""
                    className="w-full h-full object-cover rounded-md"
                  />
                  <button
                    onClick={() => handleDeleteImage(img.id)}
                    className="absolute top-1 right-1 bg-destructive text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {(!imgDetail.imagenes || imgDetail.imagenes.length === 0) && (
                <div className="col-span-3 text-center text-sm text-muted-foreground py-8">Sin imágenes</div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleUploadImage} className="hidden" />
            <Button variant="outline" className="w-full" disabled={uploading}
              onClick={() => fileRef.current?.click()}>
              <Upload className="w-4 h-4" />
              {uploading ? "Subiendo…" : "Agregar imagen"}
            </Button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

// ─── CLIENTES TAB ────────────────────────────────────────────────────────────
function ClientesTab() {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [open,    setOpen]    = useState(false);
  const [edit,    setEdit]    = useState(null);
  const [saving,  setSaving]  = useState(false);

  const emptyForm = () => ({ nombre: "", ruc_ci: "", telefono: "", email: "", direccion: "", tipo: "PERSONA" });
  const [form, setForm] = useState(emptyForm());
  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const r = await api.get("/bazar/clientes", { params: search ? { q: search } : {} });
      setList(r.data);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  function openCreate() { setEdit(null); setForm(emptyForm()); setOpen(true); }
  function openEdit(c) {
    setEdit(c);
    setForm({
      nombre: c.nombre, ruc_ci: c.ruc_ci ?? "", telefono: c.telefono ?? "",
      email: c.email ?? "", direccion: c.direccion ?? "", tipo: c.tipo,
    });
    setOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, ruc_ci: form.ruc_ci || null, email: form.email || null };
      if (edit) {
        await api.put(`/bazar/clientes/${edit.id}`, body);
        toast.success("Cliente actualizado");
      } else {
        await api.post("/bazar/clientes", body);
        toast.success("Cliente creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(c) {
    if (!window.confirm(`¿Eliminar cliente "${c.nombre}"?`)) return;
    await api.delete(`/bazar/clientes/${c.id}`);
    toast.success("Cliente eliminado");
    load();
  }

  if (loading) return <div className="flex justify-center p-16"><Spinner className="w-8 h-8" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar por nombre o RUC/CI…" className="flex-1 sm:max-w-xs" />
        <Button onClick={openCreate}><Plus className="w-4 h-4" />Nuevo cliente</Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>RUC / CI</TableHead>
            <TableHead>Teléfono</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.length === 0 && (
            <TableRow><TableCell colSpan={5}><Empty message="Sin clientes registrados" /></TableCell></TableRow>
          )}
          {list.map((c) => (
            <TableRow key={c.id}>
              <TableCell className="font-medium">{c.nombre}</TableCell>
              <TableCell className="font-mono text-xs">{c.ruc_ci ?? "—"}</TableCell>
              <TableCell>{c.telefono ?? "—"}</TableCell>
              <TableCell><Badge variant="secondary">{c.tipo}</Badge></TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => openEdit(c)}>
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(c)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} title={edit ? "Editar cliente" : "Nuevo cliente"}>
        <form onSubmit={handleSubmit} className="space-y-3">
          <FormField label="Nombre *">
            <Input value={form.nombre} onChange={(e) => s("nombre", e.target.value)} required />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Tipo">
              <Select value={form.tipo} onChange={(e) => s("tipo", e.target.value)}>
                <option value="PERSONA">Persona física</option>
                <option value="EMPRESA">Empresa</option>
              </Select>
            </FormField>
            <FormField label="RUC / CI">
              <Input value={form.ruc_ci} onChange={(e) => s("ruc_ci", e.target.value)} />
            </FormField>
            <FormField label="Teléfono">
              <Input value={form.telefono} onChange={(e) => s("telefono", e.target.value)} />
            </FormField>
            <FormField label="Email">
              <Input type="email" value={form.email} onChange={(e) => s("email", e.target.value)} />
            </FormField>
          </div>
          <FormField label="Dirección">
            <Input value={form.direccion} onChange={(e) => s("direccion", e.target.value)} />
          </FormField>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={saving}>Guardar</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}

// ─── PRESUPUESTOS TAB ────────────────────────────────────────────────────────
function PresupuestosTab() {
  const [list,           setList]           = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState("");
  const [open,           setOpen]           = useState(false);
  const [detail,         setDetail]         = useState(null);
  const [productos,      setProductos]      = useState([]);
  const [clientes,       setClientes]       = useState([]);
  const [saving,         setSaving]         = useState(false);
  const [clientSearch,   setClientSearch]   = useState("");
  const [showClientDrop, setShowClientDrop] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClient,      setNewClient]      = useState({ nombre: "", ruc_ci: "", telefono: "", email: "", direccion: "", tipo: "PERSONA" });
  const [savingClient,   setSavingClient]   = useState(false);
  const clientDropRef = useRef(null);

  const emptyForm = () => ({
    cliente_id: "", cliente_nombre: "", observaciones: "", descuento_monto: 0, items: [],
  });
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    try {
      const r = await api.get("/bazar/presupuestos", { params: search ? { q: search } : {} });
      setList(r.data);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    function onMouseDown(e) {
      if (clientDropRef.current && !clientDropRef.current.contains(e.target)) setShowClientDrop(false);
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  async function openCreate() {
    const [p, c] = await Promise.all([api.get("/bazar/productos"), api.get("/bazar/clientes")]);
    setProductos(p.data);
    setClientes(c.data);
    setForm(emptyForm());
    setClientSearch("");
    setCreatingClient(false);
    setNewClient({ nombre: "", ruc_ci: "", telefono: "", email: "", direccion: "", tipo: "PERSONA" });
    setOpen(true);
  }

  async function loadDetail(pre) {
    const r = await api.get(`/bazar/presupuestos/${pre.id}`);
    setDetail(r.data);
  }

  function addItem() {
    setForm((f) => ({
      ...f,
      items: [...f.items, { producto_id: "", descripcion: "", cantidad: 1, precio_unitario: 0 }],
    }));
  }

  function removeItem(i) {
    setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  }

  function updateItem(i, field, val) {
    setForm((f) => ({
      ...f,
      items: f.items.map((item, idx) => {
        if (idx !== i) return item;
        const updated = { ...item, [field]: val };
        if (field === "producto_id") {
          const prod = productos.find((p) => p.id === Number(val));
          if (prod) {
            updated.descripcion = prod.nombre;
            updated.precio_unitario = prod.precio_venta;
          }
        }
        return updated;
      }),
    }));
  }

  const selectedCliente = clientes.find((c) => c.id === Number(form.cliente_id));
  const filteredClientes = clientes.filter((c) => {
    if (!clientSearch.trim()) return true;
    const q = clientSearch.toLowerCase();
    return c.nombre.toLowerCase().includes(q) || (c.ruc_ci && c.ruc_ci.includes(clientSearch));
  });

  async function createNewClient() {
    if (!newClient.nombre.trim()) { toast.error("Nombre requerido"); return; }
    setSavingClient(true);
    try {
      const r = await api.post("/bazar/clientes", { ...newClient, ruc_ci: newClient.ruc_ci || null, email: newClient.email || null });
      toast.success("Cliente creado");
      const upd = await api.get("/bazar/clientes");
      setClientes(upd.data);
      setForm((f) => ({ ...f, cliente_id: String(r.data.id) }));
      setCreatingClient(false);
      setNewClient({ nombre: "", ruc_ci: "", telefono: "", email: "", direccion: "", tipo: "PERSONA" });
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al crear cliente");
    } finally {
      setSavingClient(false);
    }
  }

  const subtotal = form.items.reduce((s, i) => s + Number(i.cantidad || 0) * Number(i.precio_unitario || 0), 0);
  const total = Math.max(0, subtotal - Number(form.descuento_monto || 0));

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.items.length === 0) { toast.error("Agregue al menos un ítem"); return; }
    setSaving(true);
    try {
      await api.post("/bazar/presupuestos", {
        cliente_id:      form.cliente_id ? Number(form.cliente_id) : null,
        cliente_nombre:  form.cliente_nombre || null,
        observaciones:   form.observaciones || null,
        descuento_monto: Number(form.descuento_monto),
        items: form.items.map((i) => ({
          producto_id:     i.producto_id ? Number(i.producto_id) : null,
          descripcion:     i.descripcion,
          cantidad:        Number(i.cantidad),
          precio_unitario: Number(i.precio_unitario),
        })),
      });
      toast.success("Presupuesto creado");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(pre) {
    if (!window.confirm(`¿Eliminar presupuesto ${pre.numero}?`)) return;
    await api.delete(`/bazar/presupuestos/${pre.id}`);
    toast.success("Presupuesto eliminado");
    load();
  }

  function printPresupuesto(d) {
    const items = (d.items || []).map((i) => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${i.descripcion}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:center">${Number(i.cantidad)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right">${fmt(i.precio_unitario)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right">${fmt(i.subtotal)}</td>
      </tr>`).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>Presupuesto ${d.numero}</title>
      <style>
        body{font-family:Arial,sans-serif;font-size:13px;color:#111;margin:0;padding:32px}
        h1{font-size:22px;margin:0 0 4px}
        .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px}
        .empresa{font-size:11px;color:#6b7280;margin-top:4px}
        .numero{font-size:14px;font-weight:700;color:#1d4ed8}
        .info{background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:12px 16px;margin-bottom:20px;font-size:12px;line-height:1.7}
        .info span{color:#6b7280}
        table{width:100%;border-collapse:collapse;margin-bottom:16px}
        thead th{background:#f3f4f6;padding:7px 8px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.05em;border-bottom:2px solid #d1d5db}
        .totales{text-align:right;font-size:13px;line-height:1.8}
        .total-final{font-size:18px;font-weight:700}
        .obs{margin-top:16px;font-size:12px;color:#374151;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:10px 14px}
        .footer{margin-top:40px;font-size:10px;color:#9ca3af;text-align:center}
        .watermark{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);width:55%;max-width:420px;opacity:0.11;pointer-events:none;z-index:0}
      </style></head><body>
      <img class="watermark" src="${window.location.origin}/watermark.png" alt=""/>
      <div class="header">
        <div>
          <h1>Bazar</h1>
          <div class="empresa">Gestión independiente de productos, ventas y finanzas</div>
        </div>
        <div style="text-align:right">
          <div class="numero">${d.numero}</div>
          <div style="font-size:11px;color:#6b7280;margin-top:2px">PRESUPUESTO</div>
        </div>
      </div>
      <div class="info">
        <div><span>Cliente: </span>${d.nombre_cliente || d.cliente_nombre || "—"}</div>
        <div><span>Fecha: </span>${new Date(d.fecha).toLocaleDateString("es-PY")}</div>
        <div><span>Estado: </span>${d.estado}</div>
      </div>
      <table>
        <thead><tr>
          <th>Descripción</th>
          <th style="text-align:center">Cant.</th>
          <th style="text-align:right">Precio</th>
          <th style="text-align:right">Subtotal</th>
        </tr></thead>
        <tbody>${items}</tbody>
      </table>
      <div class="totales">
        <div>Subtotal: ${fmt(d.subtotal)}</div>
        ${Number(d.descuento_monto) > 0 ? `<div style="color:#dc2626">Descuento: -${fmt(d.descuento_monto)}</div>` : ""}
        <div class="total-final">Total: ${fmt(d.total)}</div>
      </div>
      ${d.observaciones ? `<div class="obs"><strong>Observaciones:</strong> ${d.observaciones}</div>` : ""}
      <div class="footer">Documento generado el ${new Date().toLocaleDateString("es-PY")}</div>
      <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();}}<\/script>
    </body></html>`;

    const w = window.open("", "_blank", "width=800,height=600");
    w.document.write(html);
    w.document.close();
  }

  if (loading) return <div className="flex justify-center p-16"><Spinner className="w-8 h-8" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar presupuestos…" className="flex-1 sm:max-w-xs" />
        <Button onClick={openCreate}><Plus className="w-4 h-4" />Nuevo presupuesto</Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Número</TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead>Cliente</TableHead>
            <TableHead>Subtotal</TableHead>
            <TableHead>Total</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.length === 0 && (
            <TableRow><TableCell colSpan={7}><Empty message="Sin presupuestos" /></TableCell></TableRow>
          )}
          {list.map((pre) => (
            <TableRow key={pre.id}>
              <TableCell className="font-mono text-xs">{pre.numero}</TableCell>
              <TableCell className="text-xs">{new Date(pre.fecha).toLocaleDateString()}</TableCell>
              <TableCell>{pre.nombre_cliente || pre.cliente_nombre || "—"}</TableCell>
              <TableCell>{fmt(pre.subtotal)}</TableCell>
              <TableCell className="font-semibold">{fmt(pre.total)}</TableCell>
              <TableCell><Badge variant="secondary">{pre.estado}</Badge></TableCell>
              <TableCell>
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => loadDetail(pre)}>
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(pre)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Dialog nuevo presupuesto */}
      <Dialog open={open} onClose={() => { setOpen(false); setClientSearch(""); setCreatingClient(false); }} title="Nuevo presupuesto" size="xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <div className="relative" ref={clientDropRef}>
              <FormField label="Cliente">
                <div className="relative">
                  <Input
                    value={selectedCliente
                      ? `${selectedCliente.nombre}${selectedCliente.ruc_ci ? ` — ${selectedCliente.ruc_ci}` : ""}`
                      : clientSearch}
                    onChange={(e) => { setClientSearch(e.target.value); setForm((f) => ({ ...f, cliente_id: "" })); setShowClientDrop(true); }}
                    onFocus={() => setShowClientDrop(true)}
                    placeholder="Buscar por nombre o CI/RUC…"
                    readOnly={!!selectedCliente}
                  />
                  {(clientSearch || form.cliente_id) && (
                    <button type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => { setClientSearch(""); setForm((f) => ({ ...f, cliente_id: "" })); setShowClientDrop(false); }}>
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </FormField>
              {showClientDrop && (
                <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-52 overflow-y-auto">
                  {filteredClientes.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Sin resultados</div>}
                  {filteredClientes.map((c) => (
                    <button key={c.id} type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setForm((f) => ({ ...f, cliente_id: String(c.id) })); setClientSearch(""); setShowClientDrop(false); }}>
                      {c.nombre}{c.ruc_ci ? <span className="text-muted-foreground"> — {c.ruc_ci}</span> : null}
                    </button>
                  ))}
                  <div className="border-t border-border">
                    <button type="button"
                      className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-muted flex items-center gap-1.5 transition-colors"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setShowClientDrop(false); setCreatingClient(true); }}>
                      <Plus className="w-3.5 h-3.5" />Crear nuevo cliente
                    </button>
                  </div>
                </div>
              )}
            </div>
            {creatingClient && (
              <div className="border border-border rounded-lg p-3 space-y-2 bg-muted/20">
                <p className="text-sm font-semibold">Nuevo cliente</p>
                <FormField label="Nombre *">
                  <Input value={newClient.nombre} onChange={(e) => setNewClient((n) => ({ ...n, nombre: e.target.value }))} />
                </FormField>
                <div className="grid grid-cols-2 gap-2">
                  <FormField label="Tipo">
                    <Select value={newClient.tipo} onChange={(e) => setNewClient((n) => ({ ...n, tipo: e.target.value }))}>
                      <option value="PERSONA">Persona física</option>
                      <option value="EMPRESA">Empresa</option>
                    </Select>
                  </FormField>
                  <FormField label="CI / RUC">
                    <Input value={newClient.ruc_ci} onChange={(e) => setNewClient((n) => ({ ...n, ruc_ci: e.target.value }))} />
                  </FormField>
                  <FormField label="Teléfono">
                    <Input value={newClient.telefono} onChange={(e) => setNewClient((n) => ({ ...n, telefono: e.target.value }))} />
                  </FormField>
                  <FormField label="Email">
                    <Input type="email" value={newClient.email} onChange={(e) => setNewClient((n) => ({ ...n, email: e.target.value }))} />
                  </FormField>
                </div>
                <FormField label="Dirección">
                  <Input value={newClient.direccion} onChange={(e) => setNewClient((n) => ({ ...n, direccion: e.target.value }))} />
                </FormField>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" size="sm" onClick={() => setCreatingClient(false)}>Cancelar</Button>
                  <Button type="button" size="sm" loading={savingClient} onClick={createNewClient}>Guardar cliente</Button>
                </div>
              </div>
            )}
          </div>
          <FormField label="Observaciones">
            <Textarea value={form.observaciones} rows={2}
              onChange={(e) => setForm((f) => ({ ...f, observaciones: e.target.value }))} />
          </FormField>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Ítems del presupuesto</Label>
              <Button type="button" size="sm" variant="outline" onClick={addItem}>
                <Plus className="w-3.5 h-3.5" />Agregar ítem
              </Button>
            </div>
            {form.items.length > 0 && (
              <div className="max-h-52 overflow-y-auto overflow-x-auto pr-1">
                <div className="min-w-[440px]">
                {form.items.map((item, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 items-center">
                    <div className="col-span-4">
                      <ProductoCombo
                        productos={productos}
                        value={item.producto_id}
                        onChange={(val) => updateItem(i, "producto_id", val)}
                      />
                    </div>
                    <div className="col-span-3">
                      <Input className="h-9" placeholder="Descripción"
                        value={item.descripcion} onChange={(e) => updateItem(i, "descripcion", e.target.value)} />
                    </div>
                    <div className="col-span-1">
                      <Input className="h-9" type="number" min="0.001" step="0.001" placeholder="Cant."
                        value={item.cantidad} onChange={(e) => updateItem(i, "cantidad", e.target.value)} />
                    </div>
                    <div className="col-span-3">
                      <Input className="h-9" type="number" min="0" step="0.01" placeholder="Precio"
                        value={item.precio_unitario} onChange={(e) => updateItem(i, "precio_unitario", e.target.value)} />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <Button type="button" size="sm" variant="ghost" className="h-9 w-9 p-0"
                        onClick={() => removeItem(i)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
                </div>
              </div>
            )}
            {form.items.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4 border border-dashed border-border rounded-md">
                Sin ítems — presione "Agregar ítem"
              </p>
            )}
          </div>

          <div className="flex items-end justify-between gap-4 bg-muted/30 rounded-lg p-3">
            <FormField label="Descuento" className="w-36">
              <Input type="number" min="0" step="0.01" value={form.descuento_monto}
                onChange={(e) => setForm((f) => ({ ...f, descuento_monto: e.target.value }))} />
            </FormField>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Subtotal: {fmt(subtotal)}</p>
              <p className="text-xl font-bold">Total: {fmt(total)}</p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={saving}>Crear presupuesto</Button>
          </div>
        </form>
      </Dialog>

      {/* Sheet detalle presupuesto */}
      <Sheet open={!!detail} onClose={() => setDetail(null)} title={detail ? `Presupuesto ${detail.numero}` : ""}>
        {detail && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => printPresupuesto(detail)}>
                <Printer className="w-4 h-4" />Imprimir / PDF
              </Button>
            </div>
            <Card>
              <CardContent className="pt-4 text-sm space-y-1">
                <p><span className="text-muted-foreground">Cliente: </span>{detail.nombre_cliente || detail.cliente_nombre || "—"}</p>
                <p><span className="text-muted-foreground">Fecha: </span>{new Date(detail.fecha).toLocaleDateString()}</p>
                <p><span className="text-muted-foreground">Estado: </span><Badge variant="secondary">{detail.estado}</Badge></p>
                {detail.observaciones && <p><span className="text-muted-foreground">Obs: </span>{detail.observaciones}</p>}
              </CardContent>
            </Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Cant.</TableHead>
                  <TableHead>Precio</TableHead>
                  <TableHead>Subtotal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.items?.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell>{i.descripcion}</TableCell>
                    <TableCell>{i.cantidad}</TableCell>
                    <TableCell>{fmt(i.precio_unitario)}</TableCell>
                    <TableCell>{fmt(i.subtotal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="text-right space-y-1 text-sm bg-muted/30 rounded-lg p-3">
              <p>Subtotal: {fmt(detail.subtotal)}</p>
              {Number(detail.descuento_monto) > 0 && (
                <p className="text-destructive">Descuento: -{fmt(detail.descuento_monto)}</p>
              )}
              <p className="text-lg font-bold">Total: {fmt(detail.total)}</p>
            </div>
          </div>
        )}
      </Sheet>
    </div>
  );
}

// ─── PRODUCTO COMBO ──────────────────────────────────────────────────────────
function ProductoCombo({ productos, value, onChange }) {
  const [search,   setSearch]   = useState("");
  const [showDrop, setShowDrop] = useState(false);
  const [dropPos,  setDropPos]  = useState({ top: 0, left: 0, width: 0 });
  const wrapRef   = useRef(null);
  const portalRef = useRef(null);

  const selected = productos.find((p) => p.id === Number(value));
  const filtered = productos.filter((p) =>
    !search.trim() || p.nombre.toLowerCase().includes(search.toLowerCase())
  );

  function openDrop() {
    if (wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 2, left: r.left, width: r.width });
    }
    setShowDrop(true);
  }

  useEffect(() => {
    function onMD(e) {
      const inWrap   = wrapRef.current   && wrapRef.current.contains(e.target);
      const inPortal = portalRef.current && portalRef.current.contains(e.target);
      if (!inWrap && !inPortal) setShowDrop(false);
    }
    document.addEventListener("mousedown", onMD);
    return () => document.removeEventListener("mousedown", onMD);
  }, []);

  return (
    <div ref={wrapRef}>
      <div className="relative">
        <Input
          className="h-9 pr-6 text-xs"
          value={selected ? selected.nombre : search}
          onChange={(e) => { setSearch(e.target.value); onChange(""); openDrop(); }}
          onFocus={openDrop}
          placeholder="Buscar…"
          readOnly={!!selected}
        />
        {(search || value) && (
          <button type="button"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={() => { setSearch(""); onChange(""); setShowDrop(false); }}>
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {showDrop && createPortal(
        <div
          ref={portalRef}
          style={{ position: "fixed", top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999 }}
          className="bg-background border border-border rounded-md shadow-lg max-h-40 overflow-y-auto"
        >
          {filtered.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Sin resultados</div>}
          {filtered.map((p) => (
            <button key={p.id} type="button"
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(String(p.id)); setSearch(""); setShowDrop(false); }}>
              {p.nombre}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── VENTAS TAB ──────────────────────────────────────────────────────────────
function VentasTab() {
  const [list,          setList]          = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState("");
  const [open,          setOpen]          = useState(false);
  const [detail,        setDetail]        = useState(null);
  const [productos,     setProductos]     = useState([]);
  const [clientes,      setClientes]      = useState([]);
  const [saving,        setSaving]        = useState(false);
  const [clientSearch,  setClientSearch]  = useState("");
  const [showClientDrop, setShowClientDrop] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClient,     setNewClient]     = useState({ nombre: "", ruc_ci: "", telefono: "", email: "", direccion: "", tipo: "PERSONA" });
  const [savingClient,  setSavingClient]  = useState(false);
  const clientDropRef = useRef(null);

  const emptyForm = () => ({
    cliente_id: "", cliente_nombre: "", observaciones: "", descuento_monto: 0, items: [],
  });
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    try {
      const r = await api.get("/bazar/ventas", { params: search ? { q: search } : {} });
      setList(r.data);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    function onMouseDown(e) {
      if (clientDropRef.current && !clientDropRef.current.contains(e.target)) {
        setShowClientDrop(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  async function openCreate() {
    const [p, c] = await Promise.all([api.get("/bazar/productos"), api.get("/bazar/clientes")]);
    setProductos(p.data);
    setClientes(c.data);
    setForm(emptyForm());
    setClientSearch("");
    setCreatingClient(false);
    setNewClient({ nombre: "", ruc_ci: "", telefono: "", email: "", direccion: "", tipo: "PERSONA" });
    setOpen(true);
  }

  async function loadDetail(ven) {
    const r = await api.get(`/bazar/ventas/${ven.id}`);
    setDetail(r.data);
  }

  function addItem() {
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        producto_id: "", descripcion: "", cantidad: 1,
        precio_costo: 0, precio_unitario: 0, precio_final: 0,
      }],
    }));
  }

  function removeItem(i) {
    setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  }

  function updateItem(i, field, val) {
    setForm((f) => ({
      ...f,
      items: f.items.map((item, idx) => {
        if (idx !== i) return item;
        const updated = { ...item, [field]: val };
        if (field === "producto_id") {
          const prod = productos.find((p) => p.id === Number(val));
          if (prod) {
            updated.descripcion = prod.nombre;
            updated.precio_costo = prod.precio_costo;
            updated.precio_unitario = prod.precio_venta;
            updated.precio_final = prod.precio_venta;
          }
        }
        return updated;
      }),
    }));
  }

  const selectedCliente = clientes.find((c) => c.id === Number(form.cliente_id));
  const filteredClientes = clientes.filter((c) => {
    if (!clientSearch.trim()) return true;
    const q = clientSearch.toLowerCase();
    return c.nombre.toLowerCase().includes(q) || (c.ruc_ci && c.ruc_ci.includes(clientSearch));
  });

  async function createNewClient() {
    if (!newClient.nombre.trim()) { toast.error("Nombre requerido"); return; }
    setSavingClient(true);
    try {
      const r = await api.post("/bazar/clientes", {
        ...newClient,
        ruc_ci: newClient.ruc_ci || null,
        email: newClient.email || null,
      });
      toast.success("Cliente creado");
      const upd = await api.get("/bazar/clientes");
      setClientes(upd.data);
      setForm((f) => ({ ...f, cliente_id: String(r.data.id), cliente_nombre: "" }));
      setCreatingClient(false);
      setNewClient({ nombre: "", ruc_ci: "", telefono: "", email: "", direccion: "", tipo: "PERSONA" });
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al crear cliente");
    } finally {
      setSavingClient(false);
    }
  }

  const subtotal   = form.items.reduce((s, i) => s + Number(i.cantidad || 0) * Number(i.precio_final || 0), 0);
  const costoTotal = form.items.reduce((s, i) => s + Number(i.cantidad || 0) * Number(i.precio_costo || 0), 0);
  const total      = Math.max(0, subtotal - Number(form.descuento_monto || 0));
  const ganancia   = total - costoTotal;

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.items.length === 0) { toast.error("Agregue al menos un ítem"); return; }
    setSaving(true);
    try {
      await api.post("/bazar/ventas", {
        cliente_id:      form.cliente_id ? Number(form.cliente_id) : null,
        cliente_nombre:  form.cliente_nombre || null,
        observaciones:   form.observaciones || null,
        descuento_monto: Number(form.descuento_monto),
        items: form.items.map((i) => ({
          producto_id:     i.producto_id ? Number(i.producto_id) : null,
          descripcion:     i.descripcion,
          cantidad:        Number(i.cantidad),
          precio_costo:    Number(i.precio_costo),
          precio_unitario: Number(i.precio_unitario),
          precio_final:    Number(i.precio_final),
        })),
      });
      toast.success("Venta registrada");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(ven) {
    if (!window.confirm(`¿Anular venta ${ven.numero}? El stock será revertido.`)) return;
    await api.delete(`/bazar/ventas/${ven.id}`);
    toast.success("Venta anulada y stock revertido");
    load();
  }

  if (loading) return <div className="flex justify-center p-16"><Spinner className="w-8 h-8" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar ventas…" className="flex-1 sm:max-w-xs" />
        <Button onClick={openCreate}><Plus className="w-4 h-4" />Nueva venta</Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Número</TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead>Cliente</TableHead>
            <TableHead>Total</TableHead>
            <TableHead>Costo</TableHead>
            <TableHead>Ganancia</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.length === 0 && (
            <TableRow><TableCell colSpan={7}><Empty message="Sin ventas registradas" /></TableCell></TableRow>
          )}
          {list.map((v) => (
            <TableRow key={v.id}>
              <TableCell className="font-mono text-xs">{v.numero}</TableCell>
              <TableCell className="text-xs">{new Date(v.fecha).toLocaleDateString()}</TableCell>
              <TableCell>{v.nombre_cliente || v.cliente_nombre || "—"}</TableCell>
              <TableCell className="font-semibold">{fmt(v.total)}</TableCell>
              <TableCell className="text-muted-foreground">{fmt(v.costo_total)}</TableCell>
              <TableCell className={`font-semibold ${Number(v.ganancia) >= 0 ? "text-green-600" : "text-red-500"}`}>
                {fmt(v.ganancia)}
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => loadDetail(v)}>
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(v)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Dialog nueva venta */}
      <Dialog open={open} onClose={() => { setOpen(false); setClientSearch(""); setCreatingClient(false); }} title="Nueva venta" size="2xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            {/* Searchable client combobox */}
            <div className="relative" ref={clientDropRef}>
              <FormField label="Cliente registrado">
                <div className="relative">
                  <Input
                    value={selectedCliente
                      ? `${selectedCliente.nombre}${selectedCliente.ruc_ci ? ` — ${selectedCliente.ruc_ci}` : ""}`
                      : clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setForm((f) => ({ ...f, cliente_id: "" }));
                      setShowClientDrop(true);
                    }}
                    onFocus={() => setShowClientDrop(true)}
                    placeholder="Buscar por nombre o CI/RUC…"
                    readOnly={!!selectedCliente}
                  />
                  {(clientSearch || form.cliente_id) && (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setClientSearch("");
                        setForm((f) => ({ ...f, cliente_id: "" }));
                        setShowClientDrop(false);
                      }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </FormField>
              {showClientDrop && (
                <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-52 overflow-y-auto">
                  {filteredClientes.length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">Sin resultados</div>
                  )}
                  {filteredClientes.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setForm((f) => ({ ...f, cliente_id: String(c.id), cliente_nombre: "" }));
                        setClientSearch("");
                        setShowClientDrop(false);
                      }}
                    >
                      {c.nombre}{c.ruc_ci ? <span className="text-muted-foreground"> — {c.ruc_ci}</span> : null}
                    </button>
                  ))}
                  <div className="border-t border-border">
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-muted flex items-center gap-1.5 transition-colors"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setShowClientDrop(false); setCreatingClient(true); }}
                    >
                      <Plus className="w-3.5 h-3.5" />Crear nuevo cliente
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Manual name — only when no registered client and not creating */}
            {!form.cliente_id && !creatingClient && (
              <FormField label="Nombre (si no está registrado)">
                <Input
                  value={form.cliente_nombre}
                  onChange={(e) => setForm((f) => ({ ...f, cliente_nombre: e.target.value }))}
                  placeholder="Nombre manual…"
                />
              </FormField>
            )}

            {/* Inline new client form */}
            {creatingClient && (
              <div className="border border-border rounded-lg p-3 space-y-2 bg-muted/20">
                <p className="text-sm font-semibold">Nuevo cliente</p>
                <FormField label="Nombre *">
                  <Input value={newClient.nombre} onChange={(e) => setNewClient((n) => ({ ...n, nombre: e.target.value }))} />
                </FormField>
                <div className="grid grid-cols-2 gap-2">
                  <FormField label="Tipo">
                    <Select value={newClient.tipo} onChange={(e) => setNewClient((n) => ({ ...n, tipo: e.target.value }))}>
                      <option value="PERSONA">Persona física</option>
                      <option value="EMPRESA">Empresa</option>
                    </Select>
                  </FormField>
                  <FormField label="CI / RUC">
                    <Input value={newClient.ruc_ci} onChange={(e) => setNewClient((n) => ({ ...n, ruc_ci: e.target.value }))} />
                  </FormField>
                  <FormField label="Teléfono">
                    <Input value={newClient.telefono} onChange={(e) => setNewClient((n) => ({ ...n, telefono: e.target.value }))} />
                  </FormField>
                  <FormField label="Email">
                    <Input type="email" value={newClient.email} onChange={(e) => setNewClient((n) => ({ ...n, email: e.target.value }))} />
                  </FormField>
                </div>
                <FormField label="Dirección">
                  <Input value={newClient.direccion} onChange={(e) => setNewClient((n) => ({ ...n, direccion: e.target.value }))} />
                </FormField>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" size="sm" onClick={() => setCreatingClient(false)}>Cancelar</Button>
                  <Button type="button" size="sm" loading={savingClient} onClick={createNewClient}>Guardar cliente</Button>
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Ítems de venta</Label>
              <Button type="button" size="sm" variant="outline" onClick={addItem}>
                <Plus className="w-3.5 h-3.5" />Agregar ítem
              </Button>
            </div>
            {form.items.length > 0 && (
              <div className="space-y-1 overflow-x-auto">
                <div className="grid grid-cols-12 gap-1 text-xs text-muted-foreground px-1 pb-1 min-w-[520px]">
                  <span className="col-span-3">Producto</span>
                  <span className="col-span-3">Descripción</span>
                  <span className="col-span-1">Cant.</span>
                  <span className="col-span-2">Costo unit.</span>
                  <span className="col-span-2">Precio final</span>
                  <span className="col-span-1">Gan.</span>
                </div>
                <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1 min-w-[520px]">
                  {form.items.map((item, i) => {
                    const gan = Number(item.cantidad || 0) * (Number(item.precio_final || 0) - Number(item.precio_costo || 0));
                    return (
                      <div key={i} className="grid grid-cols-12 gap-1 items-center">
                        <div className="col-span-3">
                          <ProductoCombo
                            productos={productos}
                            value={item.producto_id}
                            onChange={(val) => updateItem(i, "producto_id", val)}
                          />
                        </div>
                        <div className="col-span-3">
                          <Input className="h-9" value={item.descripcion}
                            onChange={(e) => updateItem(i, "descripcion", e.target.value)} />
                        </div>
                        <div className="col-span-1">
                          <Input className="h-9" type="number" min="0.001" step="0.001"
                            value={item.cantidad} onChange={(e) => updateItem(i, "cantidad", e.target.value)} />
                        </div>
                        <div className="col-span-2">
                          <Input className="h-9 text-muted-foreground" type="number" min="0" step="0.01"
                            value={item.precio_costo} onChange={(e) => updateItem(i, "precio_costo", e.target.value)} />
                        </div>
                        <div className="col-span-2">
                          <Input className="h-9" type="number" min="0" step="0.01"
                            value={item.precio_final} onChange={(e) => updateItem(i, "precio_final", e.target.value)} />
                        </div>
                        <div className="col-span-1 flex items-center justify-between pl-1">
                          <span className={`text-xs font-semibold ${gan >= 0 ? "text-green-600" : "text-red-500"}`}>
                            {fmt(gan)}
                          </span>
                          <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0"
                            onClick={() => removeItem(i)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {form.items.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4 border border-dashed border-border rounded-md">
                Sin ítems — presione "Agregar ítem"
              </p>
            )}
          </div>

          <div className="flex items-end justify-between gap-4 bg-muted/30 rounded-lg p-3">
            <FormField label="Descuento" className="w-36">
              <Input type="number" min="0" step="0.01" value={form.descuento_monto}
                onChange={(e) => setForm((f) => ({ ...f, descuento_monto: e.target.value }))} />
            </FormField>
            <div className="text-right space-y-0.5">
              <p className="text-xs text-muted-foreground">Subtotal: {fmt(subtotal)}</p>
              <p className="text-xs text-muted-foreground">Costo total: {fmt(costoTotal)}</p>
              <p className="text-base font-bold">Total: {fmt(total)}</p>
              <p className={`text-sm font-semibold ${ganancia >= 0 ? "text-green-600" : "text-red-500"}`}>
                Ganancia: {fmt(ganancia)}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={saving}>Registrar venta</Button>
          </div>
        </form>
      </Dialog>

      {/* Sheet detalle venta */}
      <Sheet open={!!detail} onClose={() => setDetail(null)} title={detail ? `Venta ${detail.numero}` : ""}>
        {detail && (
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-4 text-sm space-y-1">
                <p><span className="text-muted-foreground">Cliente: </span>{detail.nombre_cliente || detail.cliente_nombre || "—"}</p>
                <p><span className="text-muted-foreground">Fecha: </span>{new Date(detail.fecha).toLocaleDateString()}</p>
              </CardContent>
            </Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Cant.</TableHead>
                  <TableHead>Costo</TableHead>
                  <TableHead>Precio final</TableHead>
                  <TableHead>Ganancia</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.items?.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell>{i.descripcion}</TableCell>
                    <TableCell>{i.cantidad}</TableCell>
                    <TableCell className="text-muted-foreground">{fmt(i.precio_costo)}</TableCell>
                    <TableCell>{fmt(i.precio_final)}</TableCell>
                    <TableCell className={`font-semibold ${Number(i.ganancia) >= 0 ? "text-green-600" : "text-red-500"}`}>
                      {fmt(i.ganancia)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="text-right space-y-1 text-sm bg-muted/30 rounded-lg p-3">
              <p>Total: <strong>{fmt(detail.total)}</strong></p>
              <p className="text-muted-foreground">Costo total: {fmt(detail.costo_total)}</p>
              <p className={`font-bold text-base ${Number(detail.ganancia) >= 0 ? "text-green-600" : "text-red-500"}`}>
                Ganancia: {fmt(detail.ganancia)}
              </p>
            </div>
          </div>
        )}
      </Sheet>
    </div>
  );
}

// ─── FINANZAS TAB ────────────────────────────────────────────────────────────
function FinanzasTab() {
  const [resumen,   setResumen]   = useState(null);
  const [topProds,  setTopProds]  = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [desde,     setDesde]     = useState("");
  const [hasta,     setHasta]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const [r, t, h] = await Promise.all([
        api.get("/bazar/finanzas/resumen",      { params }),
        api.get("/bazar/finanzas/top-productos", { params }),
        api.get("/bazar/finanzas/historial",     { params }),
      ]);
      setResumen(r.data);
      setTopProds(t.data);
      setHistorial(h.data);
    } finally {
      setLoading(false);
    }
  }, [desde, hasta]);

  useEffect(() => { load(); }, [load]);

  const topPorGanancia = [...topProds].sort((a, b) => b.total_ganancia - a.total_ganancia).slice(0, 10);
  const maxIngreso = Math.max(...historial.map((h) => h.ingresos), 1);

  return (
    <div className="space-y-6">
      {/* Filtros de período */}
      <div className="flex items-end gap-3 flex-wrap">
        <FormField label="Desde">
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-40" />
        </FormField>
        <FormField label="Hasta">
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-40" />
        </FormField>
        {(desde || hasta) && (
          <Button variant="outline" onClick={() => { setDesde(""); setHasta(""); }}>
            Limpiar filtros
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center p-16"><Spinner className="w-8 h-8" /></div>
      ) : (
        <>
          {/* KPIs */}
          {resumen && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title="Ventas realizadas"
                value={resumen.total_ventas}
                icon={ShoppingCart}
              />
              <StatCard
                title="Ingresos totales"
                value={fmt(resumen.ingresos)}
                icon={DollarSign}
              />
              <StatCard
                title="Costo de ventas"
                value={fmt(resumen.costos)}
                icon={BarChart2}
              />
              <StatCard
                title="Ganancia neta"
                value={fmt(resumen.ganancia)}
                icon={TrendingUp}
                accent={resumen.ganancia > 0}
              />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top por volumen */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Top productos por volumen de ventas</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {topProds.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">Sin datos en el período</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Producto</TableHead>
                        <TableHead>Vendido</TableHead>
                        <TableHead>Ingresos</TableHead>
                        <TableHead>Ganancia</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {topProds.slice(0, 10).map((p, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm max-w-[140px] truncate">{p.descripcion}</TableCell>
                          <TableCell>{p.total_vendido}</TableCell>
                          <TableCell>{fmt(p.total_ingresos)}</TableCell>
                          <TableCell className={`font-semibold ${p.total_ganancia >= 0 ? "text-green-600" : "text-red-500"}`}>
                            {fmt(p.total_ganancia)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Top por ganancia */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Top productos por mayor ganancia</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {topPorGanancia.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">Sin datos en el período</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Producto</TableHead>
                        <TableHead>Ganancia</TableHead>
                        <TableHead>Ingresos</TableHead>
                        <TableHead>Vendido</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {topPorGanancia.map((p, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm max-w-[140px] truncate">{p.descripcion}</TableCell>
                          <TableCell className={`font-semibold ${p.total_ganancia >= 0 ? "text-green-600" : "text-red-500"}`}>
                            {fmt(p.total_ganancia)}
                          </TableCell>
                          <TableCell>{fmt(p.total_ingresos)}</TableCell>
                          <TableCell>{p.total_vendido}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Historial mensual */}
          {historial.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Movimiento mensual</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {historial.map((h, i) => (
                  <div key={i} className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="font-medium">{h.mes}</span>
                      <span className="text-muted-foreground">
                        {h.ventas} {h.ventas === 1 ? "venta" : "ventas"} · Ingresos: {fmt(h.ingresos)} · Ganancia: {fmt(h.ganancia)}
                      </span>
                    </div>
                    <div className="relative h-5 bg-muted rounded overflow-hidden">
                      <div
                        className="absolute left-0 top-0 h-full bg-primary/50 rounded"
                        style={{ width: `${Math.round((h.ingresos / maxIngreso) * 100)}%` }}
                      />
                      <div
                        className="absolute left-0 top-0 h-full bg-green-500/70 rounded"
                        style={{ width: `${Math.round((Math.max(0, h.ganancia) / maxIngreso) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
                <div className="flex gap-5 text-xs text-muted-foreground pt-1">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-primary/50 inline-block" />Ingresos
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-green-500/70 inline-block" />Ganancia
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── PÁGINA PRINCIPAL BAZAR ──────────────────────────────────────────────────
export default function Bazar() {
  const [tab, setTab] = useState("productos");

  return (
    <div className="p-4 sm:p-6 flex flex-col gap-4 h-full">
      <PageHeader title="Bazar" sub="Gestión independiente de productos, ventas y finanzas" />
      <TabBar active={tab} onChange={setTab} />
      <div className="flex-1 min-h-0 overflow-y-auto">
        {tab === "productos"    && <ProductosTab />}
        {tab === "clientes"     && <ClientesTab />}
        {tab === "presupuestos" && <PresupuestosTab />}
        {tab === "ventas"       && <VentasTab />}
        {tab === "finanzas"     && <FinanzasTab />}
      </div>
    </div>
  );
}
